import { BUILD_VERSION } from "./BuildInfo";

/** Records the last guard-triggered reload, so a reload that doesn't help can't loop. */
const RELOAD_GUARD_KEY = "qqt.bootReloadAt";
/** A second stale boot inside this window means reloading is not fixing it — give up. */
const GUARD_WINDOW_MS = 10 * 60 * 1000;
/** Hard ceiling on how long boot may wait for version.json before rendering anyway. */
const DEFAULT_TIMEOUT_MS = 1200;

/**
 * Query params that mean "an auth redirect just landed here". Reloading on top of one of these
 * would re-enter the OAuth exchange with a URL whose state has already been partly consumed,
 * so the guard stands down whenever one is present.
 */
const AUTH_PARAMS = [
  "code",
  "idp",
  "source",
  "return",
  "silent_failed",
  "not_authorized",
  "trace_id",
];

/** LoginPage's one-shot silent-SSO marker (`features/Login/LoginPage.tsx`). */
const SILENT_ATTEMPT_KEY = "qqt_okta_silent_attempted";

const readSession = (key: string): string | null => {
  try {
    return window.sessionStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const writeSession = (key: string, value: string): void => {
  try {
    window.sessionStorage?.setItem(key, value);
  } catch {
    /* private mode / storage disabled — proceed unguarded rather than not recovering */
  }
};

/**
 * True when this navigation definitely put bytes on the wire for the document itself, which
 * means the browser did not hand us a cached `index.html` and there is nothing to check.
 *
 * <p>`transferSize` is 0 both for a pure cache hit and (in most browsers) for a 304
 * revalidation, so this only ever lets us skip — it never produces a false "all clear".
 */
const documentCameFromNetwork = (): boolean => {
  try {
    const [nav] = performance.getEntriesByType("navigation") as PerformanceResourceTiming[];
    return !!nav && nav.transferSize > 0;
  } catch {
    return false;
  }
};

/**
 * Reloads once, before React mounts, if this tab booted from a stale cached `index.html`.
 *
 * <p>This is the automatic half of the cache-busting story. `useVersionCheck` handles a tab
 * that goes stale <i>while open</i>, and deliberately only offers a reload because the user
 * may have an unsaved quote in front of them. At boot that constraint doesn't exist yet —
 * there is no user state to destroy — so this is the one moment where reloading without
 * asking is the right call. A `location.reload()` revalidates the top-level document, so it
 * genuinely fetches the current `index.html` and with it the current bundle.
 *
 * <p>It runs before `root.render()` rather than inside a component on purpose. QQT's auth
 * bootstrap is a state machine spread across `App`, `useOktaSession` and `LoginPage`, and it
 * keeps its progress in the URL (`?code=`, `?silent_failed=1`) and in a one-shot
 * `sessionStorage` marker that suppresses the silent SSO retry. Reloading in the middle of
 * that sequence would not just restart it — it would restart it into a state where the marker
 * says "already tried", dropping the user onto the login form instead of signing them in
 * transparently. Every guard below exists to stay out of that machine's way.
 *
 * @returns true when a reload has been triggered and the caller should not mount the app.
 */
export const bootVersionGuard = async (options?: {
  timeoutMs?: number;
  enabled?: boolean;
}): Promise<boolean> => {
  const enabled = options?.enabled ?? import.meta.env.PROD;
  if (!enabled) return false;

  // An auth redirect is landing — the URL is mid-handshake. Stand down.
  const params = new URLSearchParams(window.location.search);
  if (AUTH_PARAMS.some((param) => params.has(param))) return false;

  // The silent-SSO one-shot already fired in this tab, so a reload would skip it and strand
  // the user on the login form.
  if (readSession(SILENT_ATTEMPT_KEY) === "1") return false;

  // Already reloaded recently and we're still stale: the document isn't the problem (a
  // not-yet-propagated invalidation, a caching proxy). Boot the old bundle and let
  // useVersionCheck prompt later rather than looping.
  const lastReload = Number(readSession(RELOAD_GUARD_KEY) ?? 0);
  if (Date.now() - lastReload < GUARD_WINDOW_MS) return false;

  // The document came over the wire this navigation, so it cannot be a stale cached copy.
  if (documentCameFromNetwork()) return false;

  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort(),
    options?.timeoutMs ?? DEFAULT_TIMEOUT_MS,
  );
  try {
    const url = `${import.meta.env.BASE_URL}version.json?t=${Date.now()}`.replace(/\/\/+/g, "/");
    const res = await fetch(url, { cache: "no-store", signal: controller.signal });
    if (!res.ok) return false;
    const body: { version?: string } = await res.json();
    if (!body?.version || body.version === BUILD_VERSION) return false;

    writeSession(RELOAD_GUARD_KEY, String(Date.now()));
    window.location.reload();
    return true;
  } catch {
    // Timed out, offline, or blocked. Never hold up boot for this — the running bundle works.
    return false;
  } finally {
    window.clearTimeout(timeout);
  }
};
